import { useEffect, useState } from 'react';

// T422b — one registry for the artefact overlays, and one way back.
//
// T422a moved company research into the shell with its own tiny channel.
// Adding a second channel for the next tab would be the drift this project
// keeps re-learning (T271: six things that exist exactly once), so the
// channel generalises instead: it holds WHICH artefact is open and FOR WHICH
// recommendation, and nothing else.
//
// The way back is the harder half. A modal in the shell has no page state to
// write to, and the two pages keep different caches — the tracker has `recs`
// plus `selectedRec`, the dashboard has `feed.recs` plus `similarRecs`. So
// the shell does not write anything: it PUBLISHES `{id, patch}`, and each
// mounted page applies it with the writeback it already has. That works only
// because T474 gave every producer the same patch shape; before it, the
// shell would have had to know that arg 1 of the cover-letter callback is
// `coverLetter`.
//
// Deliberately not a context, for the same reason as T422a: a provider in
// App.jsx would re-render every consumer on every change, and a page that
// never opens an artefact should pay nothing.

/** The overlays the shell can host. Closed — an unknown kind opens nothing. */
export const ARTIFACT_KINDS = Object.freeze(['company-research', 'interview-prep']);

let _open = null; // { kind, rec } | null
const _openListeners = new Set();
const _patchListeners = new Set();

function notifyOpen() {
  _openListeners.forEach((fn) => fn(_open));
}

/**
 * @param {string} kind one of ARTIFACT_KINDS
 * @param {object} rec the recommendation, or for company research anything
 *   carrying a company name
 */
export function openArtifact(kind, rec) {
  if (!ARTIFACT_KINDS.includes(kind) || !rec) return;
  _open = { kind, rec };
  notifyOpen();
}

export function closeArtifact() {
  _open = null;
  notifyOpen();
}

export function currentArtifact() {
  return _open;
}

export function useArtifact() {
  const [v, setV] = useState(_open);
  useEffect(() => {
    setV(_open);
    _openListeners.add(setV);
    return () => {
      _openListeners.delete(setV);
    };
  }, []);
  return v;
}

/**
 * The shell says what changed; every mounted page applies it its own way.
 *
 * Nobody subscribed is not an error — it is what happens on a page that does
 * not hold that row, and the server already has the write.
 */
export function publishRecPatch(id, patch) {
  if (!id || !patch) return;
  _patchListeners.forEach((fn) => fn(id, patch));
}

export function useRecPatchSubscription(apply) {
  useEffect(() => {
    if (typeof apply !== 'function') return undefined;
    _patchListeners.add(apply);
    return () => {
      _patchListeners.delete(apply);
    };
  }, [apply]);
}

// T422a kept working: company research still opens by name, and the name is
// carried as a rec-shaped value so one registry holds both.
export function openCompanyResearch(nameOrRec) {
  const company =
    typeof nameOrRec === 'string' ? nameOrRec : nameOrRec?.job?.company ?? nameOrRec?.company ?? null;
  const name = String(company ?? '').trim();
  if (!name) {
    closeArtifact();
    return;
  }
  openArtifact('company-research', { job: { company: name } });
}

export function currentCompanyResearch() {
  return _open?.kind === 'company-research' ? _open.rec?.job?.company ?? null : null;
}
